import { useEffect, useState } from "react";
import { v4 as uuidv4 } from 'uuid';

// Tooltip helper
function Tooltip({ children, text }) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative flex items-center">
      <span
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        className="cursor-pointer"
      >
        {children}
      </span>
      
    </span>
  );
}

// Level Item Component (no longer sortable)
function LevelItem({ level, index, isEditing, onUpdate, onDelete, onCopy, className, settingsFunnel }) {
  // Animation state for this level
  const [animatedPercent, setAnimatedPercent] = useState(0);
  const [prevPercent, setPrevPercent] = useState(level.percent);

  useEffect(() => {
    let raf;
    let start;
    const duration = 900; // ms
    let from = animatedPercent;
    let to = level.percent;

    // If the percent changes, animate from current value
    if (prevPercent !== level.percent) {
      setPrevPercent(level.percent);
    }

    function animate(ts) {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const value = Math.round(from + (to - from) * progress);
      setAnimatedPercent(value);
      if (progress < 1) {
        raf = requestAnimationFrame(animate);
      }
    }       
    raf = requestAnimationFrame(animate);
    return () => raf && cancelAnimationFrame(raf);
  }, [level.percent]);

  // 5 dashes, fill according to percent
  const dashCount = 5;
  const dashPercent = 100 / dashCount;
  const filledDashes = Math.floor((animatedPercent + 0.1) / dashPercent);

  // Position of indicator (circle)
  const indicatorLeft = `calc(${animatedPercent}% - 12px)`; // 12px = half of indicator size

  let bgMetric = "linear-gradient(90deg, #ff3b30 0%, #4ade80 100%)";
  if(level.color == 'red-green'){
    bgMetric = "linear-gradient(90deg, #ff3b30 0%, #4ade80 100%)";
  }else if(level.color == 'green-red'){
    bgMetric = "linear-gradient(90deg, #4ade80 0%, #ff3b30 100%)";
  }else if(level.color == 'red'){
    bgMetric = "#ff3b30";
  }else if(level.color == 'green'){
    bgMetric = "#4ade80";
  }else if(level.color == 'blue'){
    bgMetric = "#3b82f6";
  }

  let isDashed = level.style == 'dashed';
 
  return (
    <div className={`rounded-xl   relative group ${className || ''}`}   >
      {/* Action buttons - only visible when editing */}
  
      <div className="p-0 m-0">
        <div className="flex justify-between items-center  w-full" >
          <div className="w-full">
            <div className="font-semibold text-sm text-gray-800" id={`metrics-title-${level.id}`} style={{fontFamily: settingsFunnel.font, color: settingsFunnel.primaryColor}}>
              {level.title}
            </div>
            <div className="flex justify-between items-center w-full mt-1">

              <div className="text-xs text-gray-400" id={`metrics-description-${level.id}`} style={{fontFamily: settingsFunnel.font, color: settingsFunnel.textColor}}>
                {level.description}
              </div>
              <div className="text-xs text-gray-400 font-semibold" style={{fontFamily: settingsFunnel.font, color: settingsFunnel.textColor}}>
                <span id={`metrics-percent-value-${level.id}`}>{animatedPercent}</span>%
              </div>
            </div>
          
          </div>
          
        </div>
        <div className="relative p-0 m-0"  style={{ height: 28  }}>
          {/* Dashes */}
          <div className="flex justify-between items-center absolute left-0 right-0 top-1 -translate-y-1/2 z-0 px-1">
            {[...Array(dashCount)].map((_, i) => {
              const dashStyle = {
                width: "18%",
                opacity: i < filledDashes ? 1 : 0.7,
              };
              
              if (isDashed) {
                dashStyle.borderColor = i < filledDashes ? "green" : "red";
                dashStyle.background = "transparent";
              } else {
                dashStyle.background = i < filledDashes ? "#F9C7D9" : "#E5E7EB";
              }
              
              return (
                <div
                  key={i}
                  className={`h-2 transition-all duration-300 ${
                    isDashed ? 'border-2 border-dashed' : 'rounded-full'
                  }`}
                  style={{dashStyle}}
                />
              );
            })}
          </div>
          {/* Progress line (for smooth indicator movement) */}
          <div className="absolute left-0 right-0 top-1  h-2 rounded-full bg-gray-200 z-0 "           />
          <div
            className="absolute left-0 top-1   h-2 rounded-full z-10 transition-all duration-500"
            style={{
              width: `${animatedPercent}%`,
              maxWidth: "100%",
              background: bgMetric,
              backgroundSize: "100% 100%",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            }}
          />

 
        </div>

        {/* Justified words row */}

        {level.subIndicators && level.subIndicators.length > 2 && (
        <div className="flex justify-between items-center mt-0 mb-0 px-1" id={`metrics-subindicators-${level.id}`}>  
          {(level.subIndicators || []).map((word, idx) => (
            <span key={idx} className="text-xs text-gray-400 font-medium" style={{ flex: 1, textAlign: idx === 0 ? "left" : idx === 4 ? "right" : "center", fontFamily: settingsFunnel.font, color: settingsFunnel.textColor }}>
              {word}
            </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function BlockLevel({
  block,
  setBlocks,
  updateBlock,
  className,
  isEditing,
  settingsFunnel
}) {
  // Initialize levels array directly on the block like BlockCards does
  if (block.levels === undefined) {
    block.levels = [{
      id: 'level-' + uuidv4(),
      title: block?.title || "Métricas",
      description: block?.description || "Descrição da métrica",
      percent: typeof block?.percent === "number" ? block.percent : 75,
      subIndicators: block?.subIndicators || ["Baixo", "Médio", "Alto", "Ótimo", "Excelente"],
      style: block?.style || 'dashed',
      color: block?.color || 'red-green'
    }];
  }

  // Initialize levels state from block.levels
  const [levels, setLevels] = useState(() => {
    return block.levels;
  });

  // Column configuration
  const columns = block.columns || 1;

 
  // Grid configuration based on columns
  const gridClass = columns === 1 ? 'grid-cols-1' : 'grid grid-cols-2 gap-3';
  const itemClass = columns === 2 ? 'w-1/2 m-0'  : 'w-full m-0';

  return (
    <div className={`rounded-xl m-0   w-full ${className || ""}`} >
      <div className={`flex m-0 ${columns === 2 ? 'flex-row gap-3' : 'flex-col'}`}>   
      
      {levels.map((level, index) => (
        <LevelItem
          key={level.id}
          level={level}
          index={index}
          isEditing={isEditing}
          className={itemClass}
          settingsFunnel={settingsFunnel}
        />
      ))}
      </div>
    </div>
  );
}